import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { canEditProjectedNode, isProjectedNodePlacementLocked } from '../../../core/canvas/composeProjectedScene';
import { Group, Line } from 'react-konva';
import VersionedComponentRegistry from '../../../lib/diagram/VersionedComponentRegistry';
import { getShapeManifest } from '../../../lib/diagram/registries';
import { ManifestSvgNode } from './ManifestSvgNode';
import { ManifestKonvaNode } from './ManifestKonvaNode';
import {
  evaluateProfile,
  getProfile,
  getDefaultProfile,
  type ScoringResult,
} from '../../../lib/profiles';
import { useViewProfileStore } from '../../../stores/viewProfileStore';
import { StickyNoteNode } from '../StickyNoteNode';
import { BasicShapeNode } from '../../../domains/diagram/components/basic/BasicShapeNode';
import { FlowchartShapeNode } from '../../../domains/diagram/components/flowchart/FlowchartShapeNode';
import { BpmnShapeNode } from '../../../domains/diagram/components/bpmn/BpmnShapeNode';
import { Node, Edge } from '../../../core/models/scene';
import type { DiagramType } from '../../../core/tools/interfaces/ITool';
import { resolveEffectiveShapeDimension } from '../../../core/shapes/effectiveDimensions';
import { usePerspectiveStore } from '../../../stores/perspectiveStore';
import { GenogramPerspectiveProjection } from '../../../domains/genogram/services/GenogramPerspectiveProjection';
import type { TenureResult } from '../../../domains/orgchart/services/OrgChartPerspectiveProjection';
import type { OrgReportCounts } from '../../../domains/orgchart/services/orgChartReportCounts';
import { collapseIdentityForNode } from '../../../domains/orgchart/services/orgChartCollapseProjection';
import type { ScenarioStatusIndex, NodeScenarioStatus } from '../../../domains/orgchart/models/Scenario';
import { TextNode } from '../TextNode';
import { FrameNode } from '../FrameNode';
import { Logger } from '../../../utils/logger';
import { TableShapeNode } from '../../../domains/diagram/components/table/TableShapeNode';
import { ImageShapeNode } from '../../../domains/diagram/components/ImageShapeNode';
import { PdfShapeNode } from '../../../domains/diagram/components/PdfShapeNode';
import type { TableCellKey } from '../../../stores/tableCellStore';
import type Konva from 'konva';
import { OrgCollapsedDotGrid } from '../../../domains/orgchart/components/OrgCollapsedDotGrid';
import { useViewRules, type ComputedNodeStyle, type LensPalette } from '../../../domains/orgchart/hooks/useViewRules';
import { useDiagramStore } from '../../../stores/diagramStore';
import { isReportsToEdge } from '../../../domains/orgchart/services/orgChartReportCounts';
import { ORG_PERSON_CARD_SPECS } from '../../../domains/orgchart/models/orgChartShapeVariants';
import { ORG_POSITION_NODE_TYPES } from '../../../domains/orgchart/constants/orgNodeTypes';
import { GrcBadges } from '../../../domains/process/components/GrcBadges';
import { ProcessTagBadges } from '../../../domains/process/components/ProcessTagBadges';
import { resolveSecondarySelectionDotColor } from '../../../domains/orgchart/services/secondaryReportsColor';
import { useProcessViewStore } from '../../../stores/processViewStore';
import { isManifestFirstDiagramType, shouldRenderManifestFirst } from './nodesRendererDispatch';

const logger = Logger.getLoggerWithPrefix('NodesRenderer');

const warnedMissingManifestKeys = new Set<string>();
const EMPTY_LENS_NODES: Node[] = [];
const EMPTY_LENS_EDGES: Edge[] = [];

function warnMissingManifestOnce(diagramType: DiagramType, node: Node): void {
  const key = `${diagramType}:${node.type}`;
  if (warnedMissingManifestKeys.has(key)) return;
  warnedMissingManifestKeys.add(key);
  logger.warn(`no manifest for ${node.type} in ${diagramType}; first id: ${node.id}`);
}

/**
 * Attempt to resolve a shape to a manifest-driven React element.
 * Tries the node type directly, then provider-prefixed variants so that
 * RDM authored with short names (`ec2`, `rds`) matches manifests registered
 * with provider prefixes (`aws_ec2`, `azure_ec2`).
 *
 * Returns null if no manifest matches — the caller decides whether to fall
 * through to a hardcoded handler or log an unhandled warning.
 *
 * If a `scoringResult` is provided, the top-ranked rule's color is passed
 * to ManifestSvgNode as an accent override. If a `focusedRuleId` is set,
 * nodes not matching that rule are dimmed.
 */
function tryManifestRender(
  node: Node,
  diagramType: DiagramType,
  selected: boolean,
  handlers: {
    onDragStart: (id: string, pos: { x: number; y: number }) => void;
    onDragMove: (id: string, pos: { x: number; y: number }) => void;
    onDragEnd: (id: string, pos: { x: number; y: number }) => void;
    onClick: (id: string, e: any) => void;
    onDoubleClick?: (id: string, e: any) => void;
    onStartTextEdit?: (id: string, field: 'name' | 'location' | 'text', textNode: Konva.Text) => void;
  },
  scoringResult?: ScoringResult,
  focusedRuleId?: string | null,
  lensStyleResolver?: (nodeId: string) => {
    filterOpacity: number;
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
    badges: Array<{ icon?: string; color?: string; tooltip?: string }>;
    labels: Array<{ text: string; position?: string }>;
  } | undefined,
  editingTextState?: { nodeId: string; field: 'name' | 'location' | 'text'; tableCellKey?: TableCellKey } | null,
  canWrite = true,
): React.ReactNode | null {
  const candidateIds = [
    node.type,
    `aws_${node.type}`,
    `azure_${node.type}`,
    `gcp_${node.type}`,
  ];
  for (const candidateId of candidateIds) {
    const manifest = getShapeManifest(diagramType, candidateId);
    if (manifest?.renderers?.svg || manifest?.renderers?.konvaNative) {
      const scored = scoringResult?.nodes.get(node.id);
      const overrideAccent = scored?.topColor;
      const dimmed = focusedRuleId
        ? !scored?.rules.some((r) => r.id === focusedRuleId)
        : false;
      const lensStyle = lensStyleResolver?.(node.id);
      // Manifests that supply their own Konva body (e.g. compass_bubble)
      // bypass the generic ManifestSvgNode card and render directly via
      // ManifestKonvaNode. The generic card path remains the default for
      // manifests that only declare svg output.
      if (manifest.renderers?.konvaNative) {
        return (
          <ManifestKonvaNode
            key={node.id}
            node={node}
            manifest={manifest}
            selected={selected}
            dimmed={dimmed}
            onDragStart={(pos) => handlers.onDragStart(node.id, pos)}
            onDragMove={(pos) => handlers.onDragMove(node.id, pos)}
            onDragEnd={(pos) => handlers.onDragEnd(node.id, pos)}
            onClick={(e) => handlers.onClick(node.id, e)}
            onDoubleClick={handlers.onDoubleClick ? (e) => handlers.onDoubleClick!(node.id, e) : undefined}
          />
        );
      }
      return (
        <ManifestSvgNode
          key={node.id}
          node={node}
          manifest={manifest}
          selected={selected}
          overrideAccent={overrideAccent}
          dimmed={dimmed}
          lensStyle={lensStyle}
          onDragStart={(pos) => handlers.onDragStart(node.id, pos)}
          onDragMove={(pos) => handlers.onDragMove(node.id, pos)}
          onDragEnd={(pos) => handlers.onDragEnd(node.id, pos)}
          onClick={(e) => handlers.onClick(node.id, e)}
          onDoubleClick={handlers.onDoubleClick ? (e) => handlers.onDoubleClick!(node.id, e) : undefined}
          onStartTextEdit={
            handlers.onStartTextEdit
              ? (textNode) => handlers.onStartTextEdit!(node.id, 'name', textNode)
              : undefined
          }
          editingField={
            editingTextState?.nodeId === node.id && editingTextState.field === 'name'
              ? 'name'
              : null
          }
          canWrite={canWrite}
        />
      );
    }
  }
  return null;
}

interface NodesRendererProps {
  projectionLocked?: boolean;
  /** Live People Charts keep projected card placement under layout ownership. */
  lockProjectedNodePlacement?: boolean;
  nodes: Node[];
  edges: Edge[];
  diagramId?: string;
  selectedIds: Set<string>;
  diagramType?: DiagramType;
  orgChartTenure?: Map<string, TenureResult>;
  orgReportCounts?: Map<string, OrgReportCounts>;
  orgReportingTargets?: Map<string, string>;
  orgExitNodeIds?: Set<string>;
  scenarioStatusIndex?: ScenarioStatusIndex | null;
  collapsedNodeIds?: string[];
  /** Pre-collapse edges — needed for collapsed dot grid BFS */
  preCollapseEdges?: Edge[];
  onToggleOrgCollapse?: (reportingNodeId: string) => void;
  /** Optional override for dot-grid expand-one-level behavior (used by lazy CSV org charts). */
  onExpandOrgCollapsedBranch?: (collapsedReportingNodeId: string) => void;
  /** Optional CSV cache record lookup for unhydrated nodes (lazy CSV imports) */
  getCsvRecord?: (nodeId: string) => Record<string, unknown> | undefined;
  /** Optional CSV cache children lookup for unhydrated nodes (lazy CSV imports) */
  getCsvChildren?: (nodeId: string) => string[] | undefined;
  /** Optional CSV cache lookup for the secondary (dotted-line) manager of an unhydrated person. */
  getCsvSecondaryManager?: (nodeId: string) => string | null | undefined;
  enableOrgCollapseAnimation?: boolean;
  registerNodeRef: (nodeId: string, node: any | null) => void;
  onNodeClick: (nodeId: string, e: any) => void;
  onNodeDoubleClick?: (nodeId: string, e: any) => void;
  onDragStart: (nodeId: string, pos: { x: number; y: number }) => void;
  onDragMove: (nodeId: string, pos: { x: number; y: number }) => void;
  onDragEnd: (nodeId: string, pos: { x: number; y: number }) => void;
  onTransformStart: (nodeId: string, e: any) => void;
  onTransform?: (nodeId: string, e: any) => void;
  onTransformEnd: (nodeId: string, e: any) => void;
  onStartTextEdit?: (nodeId: string, field: 'name' | 'location' | 'text', textNode: any) => void;
  onStartTableCellTextEdit?: (nodeId: string, cellKey: TableCellKey, textNode: Konva.Text) => void;
  editingTextState?: { nodeId: string; field: 'name' | 'location' | 'text'; tableCellKey?: TableCellKey } | null;
  canWrite: boolean;
}

interface OrgCollapsedDotGridsProps {
  nodes: Node[];
  edges: Edge[];
  selectedIds: Set<string>;
  computedReportCounts: Map<string, OrgReportCounts>;
  collapsedNodeIds?: string[];
  /** Pre-collapse edges — needed for collapsed dot grid BFS */
  preCollapseEdges?: Edge[];
  onToggleOrgCollapse?: (reportingNodeId: string) => void;
  onExpandOrgCollapsedBranch?: (collapsedReportingNodeId: string) => void;
  /** Optional CSV cache record lookup for unhydrated nodes (lazy CSV imports) */
  getCsvRecord?: (nodeId: string) => Record<string, unknown> | undefined;
  /** Optional CSV cache children lookup for unhydrated nodes (lazy CSV imports) */
  getCsvChildren?: (nodeId: string) => string[] | undefined;
  /** Optional CSV cache lookup for the secondary (dotted-line) manager of an unhydrated person. */
  getCsvSecondaryManager?: (nodeId: string) => string | null | undefined;
  lensPalette: LensPalette | null;
}

interface OrgNodeViewRuleState {
  opacity: number;
  visible: boolean;
  style: ComputedNodeStyle;
}

const areNodesRendererPropsEqual = (prev: NodesRendererProps, next: NodesRendererProps): boolean =>
  prev.projectionLocked === next.projectionLocked &&
  prev.lockProjectedNodePlacement === next.lockProjectedNodePlacement &&
  prev.nodes === next.nodes &&
  prev.edges === next.edges &&
  prev.selectedIds === next.selectedIds &&
  prev.diagramType === next.diagramType &&
  prev.orgChartTenure === next.orgChartTenure &&
  prev.orgReportCounts === next.orgReportCounts &&
  prev.orgReportingTargets === next.orgReportingTargets &&
  prev.orgExitNodeIds === next.orgExitNodeIds &&
  prev.scenarioStatusIndex === next.scenarioStatusIndex &&
  prev.collapsedNodeIds === next.collapsedNodeIds &&
  prev.preCollapseEdges === next.preCollapseEdges &&
  prev.getCsvRecord === next.getCsvRecord &&
  prev.getCsvChildren === next.getCsvChildren &&
  prev.getCsvSecondaryManager === next.getCsvSecondaryManager &&
  prev.enableOrgCollapseAnimation === next.enableOrgCollapseAnimation &&
  prev.editingTextState === next.editingTextState &&
  prev.canWrite === next.canWrite;

const OrgCollapsedDotGrids: React.FC<OrgCollapsedDotGridsProps> = React.memo(({
  nodes,
  edges,
  selectedIds,
  computedReportCounts,
  collapsedNodeIds,
  preCollapseEdges,
  onToggleOrgCollapse,
  onExpandOrgCollapsedBranch,
  getCsvRecord,
  getCsvChildren,
  getCsvSecondaryManager,
  lensPalette,
}) => {
  // Get heatmap/display-rule colors for dot grids (org chart only)
  const { getNodeStyle, activeColorByField, colorByLegend, activeHeatmapField, heatmapLegend, isFilterActive, hasDisplayRules } = useViewRules(nodes, edges, { lensPalette });

  // Skip dot color computation entirely when no lens/filter/display rule is active (perf optimization)
  const hasActiveLens = Boolean(activeColorByField || activeHeatmapField || isFilterActive || hasDisplayRules);

  /**
   * Hidden-person node ids that secondary-report to any currently selected node.
   * Built from pre-collapse edges (relationships inside collapsed subtrees) plus
   * CSV `secondaryReportsTo` during the dot BFS. Dots use a fixed amber highlight
   * (`resolveSecondarySelectionDotColor`) rather than edge stroke color.
   */
  const secondarySelectionHighlightIds = useMemo(() => {
    const ids = new Set<string>();
    if (!selectedIds || selectedIds.size === 0) return ids;

    const edgeSource = preCollapseEdges ?? edges;
    for (const edge of edgeSource) {
      const edgeType = String(edge.type ?? '');
      const relation = String(((edge.attrs as Record<string, unknown> | undefined)?.relation) ?? '');
      const isSecondary =
        edgeType === 'dotted' ||
        edgeType === 'matrix' ||
        relation === 'dotted' ||
        relation === 'matrix' ||
        relation === 'dotted_line_reports' ||
        relation === 'matrix_reports';
      if (!isSecondary) continue;
      const fromSelected = selectedIds.has(edge.from);
      const toSelected = selectedIds.has(edge.to);
      if (!fromSelected && !toSelected) continue;
      const hiddenId = toSelected ? edge.from : edge.to;
      if (hiddenId) ids.add(hiddenId);
    }

    return ids;
  }, [edges, preCollapseEdges, selectedIds]);

  const secondarySelectionDotColor = useMemo(() => resolveSecondarySelectionDotColor(), []);

  // Build a value→color map from the colorBy legend for resolving unhydrated node colors
  const colorByValueMap = useMemo(() => {
    if (!colorByLegend) return null;
    const map = new Map<string, string>();
    for (const entry of colorByLegend) {
      map.set(entry.value, entry.color);
    }
    return map;
  }, [colorByLegend]);

  // Resolve a field value from a CSV cache record (mirrors resolveNodeField logic)
  const resolveRecordField = useCallback((record: Record<string, unknown>, fieldId: string): unknown => {
    // Direct field lookup
    if (record[fieldId] !== undefined) return record[fieldId];
    // Check custom fields array
    const customFields = record.customFields;
    if (Array.isArray(customFields)) {
      const match = customFields.find(
        (f: any) => f.id === fieldId || f.name?.toLowerCase() === fieldId.toLowerCase()
      );
      if (match) return (match as any).value;
    }
    return undefined;
  }, []);

  // Resolve dot color: try store node first, fall back to CSV cache record
  const resolveDotColor = useCallback((childId: string): string => {
    const style = getNodeStyle(childId);
    if (style.backgroundColor) return style.backgroundColor;

    // Node not in store — try CSV cache for colorBy field
    if (getCsvRecord && activeColorByField && colorByValueMap) {
      const record = getCsvRecord(childId);
      if (record) {
        const value = resolveRecordField(record, activeColorByField);
        const strValue = value != null ? String(value) : '';
        const color = colorByValueMap.get(strValue);
        if (color) return color;
      }
    }

    // CSV cache fallback for heatmap field
    if (getCsvRecord && activeHeatmapField && heatmapLegend) {
      const record = getCsvRecord(childId);
      if (record) {
        const raw = resolveRecordField(record, activeHeatmapField);
        const num = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
        if (!isNaN(num) && heatmapLegend.maxValue !== heatmapLegend.minValue) {
          const ratio = Math.max(0, Math.min(1, (num - heatmapLegend.minValue) / (heatmapLegend.maxValue - heatmapLegend.minValue)));
          const parse = (hex: string) => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
          const [r1, g1, b1] = parse(heatmapLegend.minColor);
          const [r2, g2, b2] = parse(heatmapLegend.maxColor);
          const r = Math.round(r1 + (r2 - r1) * ratio);
          const g = Math.round(g1 + (g2 - g1) * ratio);
          const b = Math.round(b1 + (b2 - b1) * ratio);
          return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        }
      }
    }

    return '#94A3B8';
  }, [getNodeStyle, getCsvRecord, activeColorByField, colorByValueMap, activeHeatmapField, heatmapLegend, resolveRecordField]);

  // Build reporting children map from pre-collapse edges (for BFS of hidden nodes)
  const preCollapseChildrenOf = useMemo(() => {
    const childrenOf = new Map<string, string[]>();
    const edgeSource = preCollapseEdges ?? edges;
    for (const edge of edgeSource) {
      if (!isReportsToEdge(edge)) continue;
      const children = childrenOf.get(edge.to) ?? [];
      children.push(edge.from);
      childrenOf.set(edge.to, children);
    }
    return childrenOf;
  }, [preCollapseEdges, edges]);

  // Collapsed dot grids — show a dot summary beneath each collapsed parent
  const collapsedDotGrids = useMemo(() => {
    if (!collapsedNodeIds || collapsedNodeIds.length === 0) return null;
    // A stored collapse id is a Position ref on Position-backed charts and a
    // raw node id elsewhere. Index both so either form resolves; keying only
    // by `node.id` missed every fold on an Atlas chart, so no grid rendered
    // even though the branches were correctly collapsed.
    const nodeByCollapseId = new Map<string, Node>();
    for (const node of nodes) {
      nodeByCollapseId.set(node.id, node);
      const identity = collapseIdentityForNode(node);
      if (identity !== node.id) nodeByCollapseId.set(identity, node);
    }
    const collapsedIdentities = new Set(collapsedNodeIds);

    return collapsedNodeIds.map((collapsedId) => {
      const parentNode = nodeByCollapseId.get(collapsedId);
      if (!parentNode) return null;
      // Report counts and the pre-collapse child index are both keyed by node
      // id, so they must be read through the resolved node rather than the ref.
      const parentNodeId = parentNode.id;
      const counts = computedReportCounts.get(parentNodeId);
      const totalHidden = counts?.totalReports ?? 0;
      if (totalHidden <= 0) return null;

      // Compute dot colors only when there is something to express visually:
      // an active lens (heatmap / colorBy / filters / display rules) OR a
      // selected node whose hidden subtree contains a secondary report. Without
      // either, every dot would be the default gray and we can skip the work.
      const hasSecondaryHighlight = secondarySelectionHighlightIds.size > 0;
      const hasCsvSecondaryLookup = Boolean(getCsvSecondaryManager);
      const needsDotColors =
        hasActiveLens || hasSecondaryHighlight || (hasCsvSecondaryLookup && selectedIds && selectedIds.size > 0);

      const dotGridVisuals: { colors: string[]; highlighted: boolean[] } | undefined = !needsDotColors
        ? undefined
        : (() => {
        const colors: string[] = [];
        const highlighted: boolean[] = [];
        const queue = [parentNodeId];
        let head = 0;
        const visited = new Set<string>();
        while (head < queue.length) {
          const current = queue[head++];
          // Use store edges first, fall back to CSV cache children for unhydrated nodes
          const children = preCollapseChildrenOf.get(current) ?? getCsvChildren?.(current) ?? [];
          for (const childId of children) {
            if (visited.has(childId)) continue;
            visited.add(childId);

            let isSecondarySelectionHighlight = secondarySelectionHighlightIds.has(childId);
            if (
              !isSecondarySelectionHighlight &&
              hasCsvSecondaryLookup &&
              selectedIds &&
              selectedIds.size > 0
            ) {
              const csvManagerId = getCsvSecondaryManager?.(childId);
              if (csvManagerId && selectedIds.has(csvManagerId)) {
                isSecondarySelectionHighlight = true;
              }
            }

            if (isSecondarySelectionHighlight) {
              colors.push(secondarySelectionDotColor);
              highlighted.push(true);
            } else if (hasActiveLens) {
              colors.push(resolveDotColor(childId));
              highlighted.push(false);
            } else {
              colors.push('#94A3B8');
              highlighted.push(false);
            }
            queue.push(childId);
          }
        }
        return { colors, highlighted };
      })();

      const parentWidth = resolveEffectiveShapeDimension(parentNode, 'width') ?? 270;
      const parentHeight = resolveEffectiveShapeDimension(parentNode, 'height') ?? 80;

      // For compact right-aligned photo nodes, avatar extends right of card; shift dot grid right to avoid overlap
      const isCompactRightPhoto =
        parentNode.type === 'org_person_photo_right' &&
        parentHeight <= 100;
      const fullAvatarSize = ORG_PERSON_CARD_SPECS.compact.image.right.size;
      const avatarRightOverflow = isCompactRightPhoto ? Math.round(fullAvatarSize * 0.6) : 0;

      // Position dot grid to the right of the parent node, vertically centered
      const dotGridX = parentNode.x + parentWidth + 8 + avatarRightOverflow;
      const dotGridY = parentNode.y;

      return (
        <OrgCollapsedDotGrid
          key={`dot-grid-${collapsedId}`}
          x={dotGridX}
          y={dotGridY}
          parentWidth={0}
          parentHeight={parentHeight}
          count={totalHidden}
          dotColors={dotGridVisuals?.colors}
          dotHighlighted={dotGridVisuals?.highlighted}
          onClick={() => {
            if (onExpandOrgCollapsedBranch) {
              onExpandOrgCollapsedBranch(collapsedId);
              return;
            }
            if (!onToggleOrgCollapse) return;
            // Expand one level only: uncollapse parent, collapse its direct children that have reports
            const directChildren = preCollapseChildrenOf.get(parentNodeId) ?? [];
            const childManagers = directChildren.filter((cid) => (preCollapseChildrenOf.get(cid)?.length ?? 0) > 0);
            // Toggle parent open
            onToggleOrgCollapse(collapsedId);
            // Collapse each direct child that is a manager (so their subtrees stay hidden)
            for (const childId of childManagers) {
              // The collapsed set stores identities, so a child must be tested
              // by its own identity rather than by its raw node id.
              const childNode = nodeByCollapseId.get(childId);
              const childIdentity = childNode ? collapseIdentityForNode(childNode) : childId;
              if (!collapsedIdentities.has(childIdentity)) {
                onToggleOrgCollapse(childId);
              }
            }
          }}
        />
      );
    });
  }, [
    collapsedNodeIds,
    nodes,
    computedReportCounts,
    onToggleOrgCollapse,
    onExpandOrgCollapsedBranch,
    preCollapseChildrenOf,
    getCsvChildren,
    getCsvSecondaryManager,
    hasActiveLens,
    resolveDotColor,
    secondarySelectionDotColor,
    secondarySelectionHighlightIds,
    selectedIds,
  ]);

  return <>{collapsedDotGrids}</>;
});

OrgCollapsedDotGrids.displayName = 'OrgCollapsedDotGrids';

export const NodesRenderer: React.FC<NodesRendererProps> = React.memo(({
  nodes,
  edges,
  diagramId,
  selectedIds,
  diagramType,
  orgChartTenure,
  orgReportCounts,
  orgReportingTargets,
  orgExitNodeIds,
  scenarioStatusIndex,
  collapsedNodeIds,
  preCollapseEdges,
  onToggleOrgCollapse,
  onExpandOrgCollapsedBranch,
  getCsvRecord,
  getCsvChildren,
  getCsvSecondaryManager,
  enableOrgCollapseAnimation,
  registerNodeRef,
  onNodeClick,
  onNodeDoubleClick,
  onDragStart,
  onDragMove,
  onDragEnd,
  onTransformStart,
  onTransform,
  onTransformEnd,
  onStartTextEdit,
  onStartTableCellTextEdit,
  editingTextState,
  canWrite: chartCanWrite,
  projectionLocked = false,
  lockProjectedNodePlacement = false,
}) => {
  const [orgHelpersReady, setOrgHelpersReady] = React.useState(false);
  const [flowchartLiveSizes, setFlowchartLiveSizes] = React.useState<Record<string, { w: number; h: number }>>({});
  const processOverlayVisibility = useProcessViewStore((s) => s.overlayVisibility);

  // Lens style resolver — pulls from useViewRules so manifest-rendered
  // shapes (network, plus any future manifest-first domain) inherit
  // the same color_by / heatmap / badge / label / filter overlays that
  // the orgchart cards have used for ages. The hook returns a no-op
  // resolver when no rules are active.
  // The cards are only what survives collapsing, but lens colours come from
  // the whole chart — as the legend and export compute them — so collapsing a
  // branch never recolours the people still on screen. A render set that is
  // not a subset of the chart (history preview, projection canvases) keeps
  // its own palette.
  const isOrgDiagram = diagramType === 'org';
  const chartNodes = useDiagramStore((state) => state.nodes);
  const fullChartLens = useViewRules(
    isOrgDiagram ? undefined : EMPTY_LENS_NODES,
    isOrgDiagram ? undefined : EMPTY_LENS_EDGES,
  );
  const renderSetIsChartSubset = useMemo(() => {
    if (!isOrgDiagram) return false;
    const chartNodeIds = new Set(chartNodes.map((node) => node.id));
    return nodes.every((node) => chartNodeIds.has(node.id));
  }, [chartNodes, isOrgDiagram, nodes]);
  const lensPalette = renderSetIsChartSubset ? fullChartLens.lensPalette : null;
  const viewRulesApi = useViewRules(nodes, edges, { lensPalette });
  const lensGetNodeStyle = viewRulesApi.getNodeStyle;
  const getNodeStyle = viewRulesApi.getNodeStyle;
  const getNodeOpacity = viewRulesApi.getNodeOpacity;
  const isNodeVisible = viewRulesApi.isNodeVisible;
  const orgHelpersRef = useRef<{
    computeOrgReportCounts: (nodes: Node[], edges: Edge[]) => Map<string, OrgReportCounts>;
    getNodeScenarioStatus: (nodeId: any, index: ScenarioStatusIndex) => NodeScenarioStatus | undefined;
    createNodeId: (id: string) => any;
  } | null>(null);

  const hasOrgNodes = useMemo(() => nodes.some((node) => node.type.startsWith('org_') || node.type === 'org-shape'), [nodes]);

  useEffect(() => {
    if (diagramType !== 'org' && !hasOrgNodes) return;
    let cancelled = false;
    (async () => {
      try {
        const reportCountsModule = await import('../../../domains/orgchart/services/orgChartReportCounts');
        const scenarioModule = await import('../../../domains/orgchart/models/Scenario');
        if (cancelled) return;
        orgHelpersRef.current = {
          computeOrgReportCounts: reportCountsModule.computeOrgReportCounts,
          getNodeScenarioStatus: scenarioModule.getNodeScenarioStatus,
          createNodeId: scenarioModule.createNodeId,
        };
        setOrgHelpersReady(true);
      } catch (e) {
        logger.warn('Failed to load org chart render helpers', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [diagramType, hasOrgNodes]);

  // Keep a stable ref to the latest registerNodeRef implementation
  const registerRefLatest = useRef(registerNodeRef);
  useEffect(() => { registerRefLatest.current = registerNodeRef; }, [registerNodeRef]);

  // Create stable per-node ref callbacks so ref identity doesn't change across renders
  const refCbMap = useRef(new Map<string, (n: any | null) => void>());
  const getStableRefCb = useCallback((id: string) => {
    const existing = refCbMap.current.get(id);
    if (existing) return existing;
    const cb = (konvaNode: any | null) => {
      // Always call the latest register function
      registerRefLatest.current(id, konvaNode);
    };
    refCbMap.current.set(id, cb);
    return cb;
  }, []);
  // Position Chart selection changes used to allocate fresh handler closures
  // for every card. That defeated PositionStaffingNode's React.memo boundary
  // and made a one-card selection reconcile the full 200-card Konva tree.
  // Stable per-id delegates keep the child props unchanged while always
  // calling the latest canvas handlers.
  const positionHandlersLatest = useRef({
    onNodeClick,
    onNodeDoubleClick,
    onDragStart,
    onDragMove,
    onDragEnd,
    onTransformStart,
    onTransform,
    onTransformEnd,
    onStartTextEdit,
  });
  positionHandlersLatest.current = {
    onNodeClick,
    onNodeDoubleClick,
    onDragStart,
    onDragMove,
    onDragEnd,
    onTransformStart,
    onTransform,
    onTransformEnd,
    onStartTextEdit,
  };
  const positionHandlersById = useRef(new Map<string, {
    onClick: (event: any) => void;
    onDoubleClick: (event: any) => void;
    onDragStart: (position: any) => void;
    onDragMove: (position: any) => void;
    onDragEnd: (position: any) => void;
    onTransformStart: (event: any) => void;
    onTransform: (event: any) => void;
    onTransformEnd: (event: any) => void;
    onStartTextEdit: (field: any, textNode: any) => void;
  }>());
  const getStablePositionHandlers = useCallback((id: string) => {
    const existing = positionHandlersById.current.get(id);
    if (existing) return existing;
    const handlers = {
      onClick: (event: any) => positionHandlersLatest.current.onNodeClick(id, event),
      onDoubleClick: (event: any) => positionHandlersLatest.current.onNodeDoubleClick?.(id, event),
      onDragStart: (position: any) => positionHandlersLatest.current.onDragStart(id, position),
      onDragMove: (position: any) => positionHandlersLatest.current.onDragMove(id, position),
      onDragEnd: (position: any) => positionHandlersLatest.current.onDragEnd(id, position),
      onTransformStart: (event: any) => positionHandlersLatest.current.onTransformStart(id, event),
      onTransform: (event: any) => positionHandlersLatest.current.onTransform?.(id, event),
      onTransformEnd: (event: any) => positionHandlersLatest.current.onTransformEnd(id, event),
      onStartTextEdit: (_field: any, textNode: any) =>
        positionHandlersLatest.current.onStartTextEdit?.(id, 'text', textNode),
    };
    positionHandlersById.current.set(id, handlers);
    return handlers;
  }, []);
  const culturePalette = usePerspectiveStore(s => s.culturePalette);
  const healthPalette = usePerspectiveStore(s => s.healthPalette);
  const projection = useMemo(() => {
    if (diagramType !== 'genogram') {
      return { culture: new Map(), health: new Map() };
    }
    try {
      const result = GenogramPerspectiveProjection.compute(nodes, edges, { culturePalette, healthPalette });

      // Debug: Log projection results
      const helenId = nodes.find(n => (n as any).name === 'Helen Anderson')?.id;
      if (helenId) {
        const cultureMix = result.culture.get(helenId);
        const healthMix = result.health.get(helenId);
        logger.debug('Projection for Helen', {
          id: helenId,
          cultureMixLength: cultureMix?.length,
          cultureMixDetails: cultureMix,
          healthMixLength: healthMix?.length,
          healthMixDetails: healthMix,
          culturePalette,
          healthPalette
        });
      }

      return result;
    } catch (e) {
      logger.warn('Projection compute failed', e);
      return { culture: new Map(), health: new Map() };
    }
  }, [diagramType, nodes, edges, culturePalette, healthPalette]);
  const {
    getNodeStyle: getViewRuleStyle,
    isNodeVisible: isViewRuleNodeVisible,
    getNodeOpacity: getViewRuleNodeOpacity,
  } = useViewRules(nodes, edges, { lensPalette });

  const getTenureFill = useCallback((nodeId: string) => {
    return orgChartTenure?.get(nodeId)?.bucket.color;
  }, [orgChartTenure]);

  const computedReportCounts = useMemo(() => {
    if (orgReportCounts) return orgReportCounts;
    if (diagramType !== 'org') return new Map();
    if (!orgHelpersRef.current?.computeOrgReportCounts) return new Map();
    try {
      return orgHelpersRef.current.computeOrgReportCounts(nodes, edges);
    } catch (e) {
      logger.warn('Org report count computation failed', e);
      return new Map();
    }
  }, [orgReportCounts, diagramType, nodes, edges, orgHelpersReady]);

  const getScenarioStatus = useCallback((nodeId: string): NodeScenarioStatus | undefined => {
    if (!scenarioStatusIndex) return undefined;
    if (!orgHelpersRef.current?.getNodeScenarioStatus || !orgHelpersRef.current?.createNodeId) return undefined;
    return orgHelpersRef.current.getNodeScenarioStatus(orgHelpersRef.current.createNodeId(nodeId), scenarioStatusIndex);
  }, [scenarioStatusIndex, orgHelpersReady]);

  const getOrgComponent = useCallback((type: string) => {
    return VersionedComponentRegistry.getComponent(type, 1, 'shape');
  }, []);

  // View profile scoring: compute once per nodes/edges/profile change and
  // use it to override node colors and dim non-matching shapes when a
  // legend entry is focused. Only active for manifest-first diagram types
  // since those are the ones that flow through ManifestSvgNode.
  const activeProfileByType = useViewProfileStore((s) => s.activeByDiagramType);
  const focusedRuleId = useViewProfileStore((s) => s.focusedRuleId);
  const scoringResult: ScoringResult | undefined = useMemo(() => {
    if (!isManifestFirstDiagramType(diagramType)) return undefined;
    const activeId =
      activeProfileByType[diagramType] ?? getDefaultProfile(diagramType)?.id;
    if (!activeId) return undefined;
    const profile = getProfile(activeId);
    if (!profile || profile.scoring.length === 0) return undefined;
    // Map Scene Node/Edge to GraphNode/GraphEdge (adapter — the runtime model
    // has extra fields beyond the universal ontology, so we pick the ones the
    // scoring engine cares about).
    const graphNodes = nodes.map((n) => ({
      id: n.id,
      type: (n as any).type ?? '',
      label: (n as any).name ?? (n as any).text ?? n.id,
      props: { ...(n as any).attrs, ...(n as any) } as Record<string, unknown>,
    }));
    const graphEdges = edges.map((e) => ({
      id: e.id,
      from: (e as any).from,
      to: (e as any).to,
      type: (e as any).type ?? '',
      props: { ...(e as any).attrs, ...(e as any) } as Record<string, unknown>,
    }));
    return evaluateProfile(profile, graphNodes, graphEdges);
  }, [diagramType, nodes, edges, activeProfileByType]);

  const orgNodeViewRuleStateById = useMemo(() => {
    if (diagramType !== 'org' && !hasOrgNodes) {
      return new Map<string, OrgNodeViewRuleState>();
    }

    const map = new Map<string, OrgNodeViewRuleState>();
    for (const node of nodes) {
      if (!node.type.startsWith('org_') && node.type !== 'org-shape') {
        continue;
      }
      map.set(node.id, {
        opacity: getNodeOpacity(node.id),
        visible: isNodeVisible(node.id),
        style: getNodeStyle(node.id),
      });
    }
    return map;
  }, [diagramType, getNodeOpacity, getNodeStyle, hasOrgNodes, isNodeVisible, nodes]);

  const renderedNodes = useMemo(() => {
    const ordered = [...nodes].sort((a, b) => (Number((a as any ).zIndex || 0)) - (Number((b as any ).zIndex || 0)));
    const elements = ordered.map((node) => {
      const canWrite = canEditProjectedNode(node, chartCanWrite, projectionLocked);
      const canDrag = canWrite && !isProjectedNodePlacementLocked(node, lockProjectedNodePlacement);
      // `frame` is a universal primitive — it isn't a shape-manifest
      // type and must render via `FrameNode` for every diagram kind,
      // including the manifest-first ones (network / compass-explorer).
      // Short-circuit here so the manifest-first dispatch below doesn't
      // reject these as "no manifest". This is what makes the Compass
      // Explorer's VPC / subnet / tier-group frames render at L3+.
      if (node.type === 'frame') {
        return (
          <FrameNode
            key={node.id}
            node={node}
            selected={selectedIds.has(node.id)}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
          />
        );
      }

      // Manifest-first dispatch (Option A). Universal canvas primitives
      // (basic shapes, sticky notes, freehand, media, tables, text, frames)
      // must keep using the shared renderers in every diagram kind; network
      // resources remain manifest-driven.
      if (diagramType && shouldRenderManifestFirst(diagramType, node.type)) {
        const rendered = tryManifestRender(
          node,
          diagramType,
          selectedIds.has(node.id),
          { onDragStart, onDragMove, onDragEnd, onClick: onNodeClick, onDoubleClick: onNodeDoubleClick, onStartTextEdit },
          scoringResult,
          focusedRuleId,
          lensGetNodeStyle,
          editingTextState,
          canWrite,
        );
        if (rendered) {
          // Network / cloud resources can carry the same controls /
          // evidence props as process shapes once federated assurance
          // data is attached, so overlay GrcBadges + ProcessTagBadges
          // here too. Both components no-op when the node has nothing
          // to render, so it's safe to call unconditionally.
          return (
            <Group key={node.id}>
              {rendered}
              <GrcBadges node={node} />
              <ProcessTagBadges node={node} />
            </Group>
          );
        }
        // No manifest found for a manifest-first domain — don't fall through
        // to the hardcoded genogram/org handlers, they'd never match anyway.
        warnMissingManifestOnce(diagramType, node);
        return null;
      }

      if (node.type === 'freehand') {
        return (
          <Line
            key={node.id}
            id={node.id}
            name="shape"
            x={(node as any).x || 0}
            y={(node as any).y || 0}
            points={(node.attrs as any)?.points || []}
            stroke={(node as any)?.strokeColor || (node.attrs as any)?.strokeColor || '#111827'}
            strokeWidth={(node.attrs as any)?.strokeWidth || 2}
            dash={(node.attrs as any)?.dash}
            dashEnabled={(node.attrs as any)?.dashEnabled ?? ((Array.isArray((node.attrs as any)?.dash) && (node.attrs as any).dash.length > 0) ? true : undefined)}
            opacity={(node.attrs as any)?.opacity ?? 1}
            lineCap={(node.attrs as any)?.lineCap || 'round'}
            lineJoin={(node.attrs as any)?.lineJoin || 'round'}
            tension={(node.attrs as any)?.tension || 0}
            hitStrokeWidth={10}
            listening={true}
            draggable
            onClick={(e: any) => onNodeClick(node.id, e)}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
            onTransform={() => {
              // For freehand shapes, we need to let the transformer work normally
              // but we'll handle the actual scaling in onTransformEnd by scaling the points array
              // Don't reset scale here - it breaks the transformer's bounds calculation
            }}
            onDragStart={(e: any) => onDragStart(node.id, { x: e.target.x(), y: e.target.y() })}
            onDragMove={(e: any) => onDragMove(node.id, { x: e.target.x(), y: e.target.y() })}
            onDragEnd={(e: any) => onDragEnd(node.id, { x: e.target.x(), y: e.target.y() })}
            ref={getStableRefCb(node.id)}
          />
        );
      }
      if (node.type === 'text') {
        const isEditingThis = editingTextState?.nodeId === node.id && editingTextState?.field === 'text';
        return (
          <TextNode
            key={node.id}
            node={node}
            selected={selectedIds.has(node.id)}
            onDragStart={(pos: { x: number; y: number }) => onDragStart(node.id, pos)}
            onDragMove={(pos: { x: number; y: number }) => onDragMove(node.id, pos)}
            onDragEnd={(pos: { x: number; y: number }) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
            onDoubleClick={onNodeDoubleClick ? (e: any) => onNodeDoubleClick(node.id, e) : undefined}
            onStartTextEdit={(textNode) => onStartTextEdit?.(node.id, 'text', textNode)}
            editing={!!isEditingThis}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
            ref={getStableRefCb(node.id)}
          />
        );
      }

      if (node.type === 'person') {
        if (diagramType === 'genogram' && !isViewRuleNodeVisible(node.id)) {
          return null;
        }

        const lensStyle = getViewRuleStyle(node.id);
        const lensOpacity = diagramType === 'genogram' ? getViewRuleNodeOpacity(node.id) : 1;
        const renderedNode: Node = {
          ...node,
          fillColor: lensStyle.backgroundColor ?? node.fillColor,
          strokeColor: lensStyle.borderColor ?? node.strokeColor,
        };
        const cultureMix = projection.culture.get(node.id) || [];
        const healthMix = projection.health.get(node.id) || [];

        // Debug: Log props being passed to PersonNode
        if ((node as any).name === 'Helen Anderson' || (node as any).name === 'Jennifer Lee') {
          logger.debug('Rendering person', {
            name: (node as any).name,
            id: node.id,
            cultureMixProp: cultureMix,
            healthMixProp: healthMix
          });
        }

        const explicitVersion = (node as any)?.version ?? (node as any)?.__shapeVersion ?? (node.attrs as any)?.__shapeVersion;
        const effectiveVersion = typeof explicitVersion === 'number' ? explicitVersion : VersionedComponentRegistry.getDefaultVersion('person', 'shape');
        const Component: any = VersionedComponentRegistry.getComponent('person', effectiveVersion, 'shape');

        return (
          <Group key={node.id} opacity={lensOpacity}>
            <Component
              node={renderedNode}
              selected={selectedIds.has(node.id)}
              onDragStart={(pos: { x: number; y: number }) => onDragStart(node.id, pos)}
              onDragMove={(pos: { x: number; y: number }) => onDragMove(node.id, pos)}
              onDragEnd={(pos: { x: number; y: number }) => onDragEnd(node.id, pos)}
              onClick={(e: any) => onNodeClick(node.id, e)}
              onDoubleClick={onNodeDoubleClick ? (e: any) => onNodeDoubleClick(node.id, e) : undefined}
              cultureMix={cultureMix}
              healthMix={healthMix}
              onTransformStart={(e: any) => onTransformStart(node.id, e)}
              canWrite={canWrite}
              onTransform={(e: any) => {
                // Live-resize vector redraw: apply scale to width/height during drag and reset node scale
                const target = e.target;
                const scaleX = target.scaleX();
                const scaleY = target.scaleY();
                const nodeData = nodes.find(n => n.id === node.id);
                if (!nodeData) return;
                const nextW = Math.max(20, (nodeData.w || 60) * scaleX);
                const nextH = Math.max(20, (nodeData.h || 60) * scaleY);

                // Find inner rotation-group and update its child width/height via attrs
                const kn = target;
                const inner = (kn as any).findOne && (kn as any).findOne('.rotation-group');
                const rectLike = inner && inner.getChildren && inner.getChildren()[0];
                if (rectLike && rectLike.width && rectLike.height) {
                  rectLike.width(nextW);
                  rectLike.height(nextH);
                }
              }}
              onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
              onStartTextEdit={onStartTextEdit ? (field: 'name' | 'location', textNode: any) => onStartTextEdit(node.id, field, textNode) : undefined}
              editingField={
                editingTextState?.nodeId === node.id && (editingTextState.field === 'name' || editingTextState.field === 'location')
                  ? editingTextState.field
                  : null
              }
              ref={getStableRefCb(node.id)}
            />
          </Group>
        );
      }

      if (node.type === 'sticky-note') {
        const explicitVersion = (node as any)?.version ?? (node as any)?.__shapeVersion ?? (node.attrs as any)?.__shapeVersion;
        const effectiveVersion = typeof explicitVersion === 'number' ? explicitVersion : VersionedComponentRegistry.getDefaultVersion('sticky-note', 'shape');
        const Component: any = VersionedComponentRegistry.getComponent('sticky-note', effectiveVersion, 'shape') || StickyNoteNode;
        const isEditingThis = editingTextState?.nodeId === node.id && editingTextState?.field === 'text';
        return (
          <Component
            key={node.id}
            node={node}
            selected={selectedIds.has(node.id)}
            editing={!!isEditingThis}
            canWrite={canWrite}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
            onDoubleClick={onNodeDoubleClick ? (e: any) => onNodeDoubleClick(node.id, e) : undefined}
            onStartTextEdit={(textNode: any) => onStartTextEdit?.(node.id, 'text', textNode)}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
            ref={getStableRefCb(node.id)}
          />
        );
      }

      if (ORG_POSITION_NODE_TYPES.has(node.type)) {
        const handlers = getStablePositionHandlers(node.id);
        const tenureFill = getTenureFill(node.id);
        const renderedNode = tenureFill ? { ...node, fillColor: tenureFill } : node;
        const explicitVersion = (renderedNode as any)?.version ?? (renderedNode as any)?.__shapeVersion ?? (renderedNode.attrs as any)?.__shapeVersion;
        const effectiveVersion = typeof explicitVersion === 'number' ? explicitVersion : VersionedComponentRegistry.getDefaultVersion(node.type, 'shape');
        const Component: any = VersionedComponentRegistry.getComponent(node.type, effectiveVersion, 'shape');
        if (!Component) return null;
        const collapseTargetId = orgReportingTargets?.get(node.id) ?? node.id;
        const isExiting = orgExitNodeIds?.has(node.id) ?? false;
        const isEditingPrimary = editingTextState?.nodeId === node.id && editingTextState?.field === 'text';
        return (
          <Component
            key={node.id}
            node={renderedNode}
            selected={selectedIds.has(node.id)}
            reportCounts={computedReportCounts.get(node.id)}
            collapseTargetId={collapseTargetId}
            onToggleCollapse={onToggleOrgCollapse}
            enableCollapseAnimation={enableOrgCollapseAnimation}
            isExiting={isExiting}
            editingPrimaryLabel={isEditingPrimary}
            scenarioStatus={getScenarioStatus(node.id)}
            viewRuleState={orgNodeViewRuleStateById.get(node.id)}
            renderDisplayRuleLabels={false}
            onDragStart={handlers.onDragStart}
            onDragMove={handlers.onDragMove}
            onDragEnd={handlers.onDragEnd}
            onClick={handlers.onClick}
            onDoubleClick={handlers.onDoubleClick}
            onTransformStart={handlers.onTransformStart}
            onTransform={handlers.onTransform}
            onTransformEnd={handlers.onTransformEnd}
            onStartTextEdit={handlers.onStartTextEdit}
            canWrite={canWrite}
            ref={getStableRefCb(node.id)}
          />
        );
      }

      if (node.type === 'org_person_photo' || node.type === 'org_person_photo_left' || node.type === 'org_person_photo_right') {
        const tenureFill = getTenureFill(node.id);
        const renderedNode = tenureFill ? { ...node, fillColor: tenureFill } : node;
        const collapseTargetId = orgReportingTargets?.get(node.id) ?? node.id;
        const isExiting = orgExitNodeIds?.has(node.id) ?? false;
        const isEditingPrimary = editingTextState?.nodeId === node.id && editingTextState?.field === 'name';
        const Component: any = getOrgComponent(node.type);
        if (!Component) return null;
        return (
          <Component
            key={node.id}
            node={renderedNode}
            selected={selectedIds.has(node.id)}
            edges={edges}
            nodes={nodes}
            reportCounts={computedReportCounts.get(node.id)}
            collapseTargetId={collapseTargetId}
            onToggleCollapse={onToggleOrgCollapse}
            enableCollapseAnimation={enableOrgCollapseAnimation}
            isExiting={isExiting}
            editingPrimaryLabel={isEditingPrimary}
            scenarioStatus={getScenarioStatus(node.id)}
            viewRuleState={orgNodeViewRuleStateById.get(node.id)}
            renderDisplayRuleLabels={false}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransform={(e: any) => onTransform?.(node.id, e)}
            onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
            onStartTextEdit={(textNode: any) => onStartTextEdit?.(node.id, 'name', textNode)}
            canWrite={canWrite}
            canDrag={canDrag}
            ref={getStableRefCb(node.id)}
          />
        );
      }

      if (node.type === 'org_person_photo_inner_left') {
        const tenureFill = getTenureFill(node.id);
        const renderedNode = tenureFill ? { ...node, fillColor: tenureFill } : node;
        const collapseTargetId = orgReportingTargets?.get(node.id) ?? node.id;
        const isExiting = orgExitNodeIds?.has(node.id) ?? false;
        const isEditingPrimary = editingTextState?.nodeId === node.id && editingTextState?.field === 'name';
        const Component: any = getOrgComponent('org_person_photo_inner_left');
        if (!Component) return null;
        return (
          <Component
            key={node.id}
            node={renderedNode}
            selected={selectedIds.has(node.id)}
            edges={edges}
            nodes={nodes}
            reportCounts={computedReportCounts.get(node.id)}
            collapseTargetId={collapseTargetId}
            onToggleCollapse={onToggleOrgCollapse}
            enableCollapseAnimation={enableOrgCollapseAnimation}
            isExiting={isExiting}
            editingPrimaryLabel={isEditingPrimary}
            viewRuleState={orgNodeViewRuleStateById.get(node.id)}
            renderDisplayRuleLabels={false}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransform={(e: any) => onTransform?.(node.id, e)}
            onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
            onStartTextEdit={(textNode: any) => onStartTextEdit?.(node.id, 'name', textNode)}
            canWrite={canWrite}
            canDrag={canDrag}
            ref={getStableRefCb(node.id)}
          />
        );
      }

      if (node.type === 'org_person_text') {
        const tenureFill = getTenureFill(node.id);
        const renderedNode = tenureFill ? { ...node, fillColor: tenureFill } : node;
        const collapseTargetId = orgReportingTargets?.get(node.id) ?? node.id;
        const isExiting = orgExitNodeIds?.has(node.id) ?? false;
        const isEditingPrimary = editingTextState?.nodeId === node.id && editingTextState?.field === 'name';
        const Component: any = getOrgComponent('org_person_text');
        if (!Component) return null;
        return (
          <Component
            key={node.id}
            node={renderedNode}
            selected={selectedIds.has(node.id)}
            edges={edges}
            nodes={nodes}
            reportCounts={computedReportCounts.get(node.id)}
            collapseTargetId={collapseTargetId}
            onToggleCollapse={onToggleOrgCollapse}
            enableCollapseAnimation={enableOrgCollapseAnimation}
            isExiting={isExiting}
            editingPrimaryLabel={isEditingPrimary}
            scenarioStatus={getScenarioStatus(node.id)}
            viewRuleState={orgNodeViewRuleStateById.get(node.id)}
            renderDisplayRuleLabels={false}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransform={(e: any) => onTransform?.(node.id, e)}
            onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
            onStartTextEdit={(textNode: any) => onStartTextEdit?.(node.id, 'name', textNode)}
            canWrite={canWrite}
            canDrag={canDrag}
            ref={getStableRefCb(node.id)}
          />
        );
      }

      if (node.type === 'org_person') {
        const tenureFill = getTenureFill(node.id);
        const renderedNode = tenureFill ? { ...node, fillColor: tenureFill } : node;
        const explicitVersion = (renderedNode as any)?.version ?? (renderedNode as any)?.__shapeVersion ?? (renderedNode.attrs as any)?.__shapeVersion;
        const effectiveVersion = typeof explicitVersion === 'number' ? explicitVersion : VersionedComponentRegistry.getDefaultVersion('org_person', 'shape');
        const Component: any = VersionedComponentRegistry.getComponent('org_person', effectiveVersion, 'shape');
        if (!Component) return null;
        const collapseTargetId = orgReportingTargets?.get(node.id) ?? node.id;
        const isExiting = orgExitNodeIds?.has(node.id) ?? false;
        const isEditingPrimary = editingTextState?.nodeId === node.id && editingTextState?.field === 'name';
        return (
          <Component
            key={node.id}
            node={renderedNode}
            selected={selectedIds.has(node.id)}
            reportCounts={computedReportCounts.get(node.id)}
            collapseTargetId={collapseTargetId}
            onToggleCollapse={onToggleOrgCollapse}
            enableCollapseAnimation={enableOrgCollapseAnimation}
            isExiting={isExiting}
            editingPrimaryLabel={isEditingPrimary}
            scenarioStatus={getScenarioStatus(node.id)}
            viewRuleState={orgNodeViewRuleStateById.get(node.id)}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
            onDoubleClick={onNodeDoubleClick ? (e: any) => onNodeDoubleClick(node.id, e) : undefined}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransform={(e: any) => onTransform?.(node.id, e)}
            onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
            onStartTextEdit={(textNode: any) => onStartTextEdit?.(node.id, 'name', textNode)}
            canWrite={canWrite}
            canDrag={canDrag}
            ref={getStableRefCb(node.id)}
          />
        );
      }

      if (node.type === 'org_department') {
        const explicitVersion = (node as any)?.version ?? (node as any)?.__shapeVersion ?? (node.attrs as any)?.__shapeVersion;
        const effectiveVersion = typeof explicitVersion === 'number' ? explicitVersion : VersionedComponentRegistry.getDefaultVersion('org_department', 'shape');
        const Component: any = VersionedComponentRegistry.getComponent('org_department', effectiveVersion, 'shape');
        if (!Component) return null;
        const isEditingPrimary = editingTextState?.nodeId === node.id && editingTextState?.field === 'name';
        return (
          <Component
            key={node.id}
            node={node}
            selected={selectedIds.has(node.id)}
            editingPrimaryLabel={isEditingPrimary}
            scenarioStatus={getScenarioStatus(node.id)}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransform={(e: any) => onTransform?.(node.id, e)}
            onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
            onStartTextEdit={(textNode: any) => onStartTextEdit?.(node.id, 'name', textNode)}
            canWrite={canWrite}
            ref={getStableRefCb(node.id)}
          />
        );
      }

      if (node.type === 'basic-shape' || node.type === 'flowchart-shape' || node.type === 'bpmn-shape' || node.type === 'org-shape') {
        const variant = (node.attrs as Record<string, unknown>)?.variant as string | undefined;
        const isBpmnVariant = node.type === 'bpmn-shape' || (typeof variant === 'string' && variant.startsWith('bpmn-'));
        const effectiveType = isBpmnVariant ? 'bpmn-shape' : node.type;
        const explicitVersion = (node as any)?.version ?? (node as any)?.__shapeVersion ?? (node.attrs as any)?.__shapeVersion;
        const defaultVersion = VersionedComponentRegistry.getDefaultVersion(effectiveType, 'shape');
        const effectiveVersion = typeof explicitVersion === 'number' ? explicitVersion : defaultVersion;
        const fallbackComponent = node.type === 'basic-shape'
          ? BasicShapeNode
          : isBpmnVariant
            ? BpmnShapeNode
            : node.type === 'flowchart-shape'
              ? FlowchartShapeNode
              : null;
        const Component: any = VersionedComponentRegistry.getComponent(effectiveType, effectiveVersion, 'shape') || fallbackComponent;
        if (!Component) return null;
        const isEditingThis = editingTextState?.nodeId === node.id && editingTextState?.field === 'text';
        const isFlowchartShape = node.type === 'flowchart-shape' || node.type === 'bpmn-shape';
        if (isFlowchartShape && !isViewRuleNodeVisible(node.id)) {
          return null;
        }
        const lensStyle = isFlowchartShape ? getViewRuleStyle(node.id) : undefined;
        const renderedNode: Node =
          isFlowchartShape && lensStyle
            ? {
                ...node,
                fillColor: lensStyle.backgroundColor ?? node.fillColor,
                strokeColor: lensStyle.borderColor ?? node.strokeColor,
                attrs: lensStyle.textColor
                  ? { ...(node.attrs ?? {}), textColor: lensStyle.textColor }
                  : node.attrs,
              }
            : node;
        const nodeOpacity = (node.attrs as any)?.opacity;
        const groupOpacity =
          (nodeOpacity ?? 1) * (isFlowchartShape ? getViewRuleNodeOpacity(node.id) : 1);
        const processOverlayProps = diagramType === 'flowchart' ? processOverlayVisibility : undefined;
        return (
          <Group key={node.id} opacity={groupOpacity}>
          <Component
            node={renderedNode}
            selected={selectedIds.has(node.id)}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransformEnd={(e: any) => {
              if (isFlowchartShape) {
                setFlowchartLiveSizes((prev) => {
                  const next = { ...prev };
                  delete next[node.id];
                  return next;
                });
              }
              onTransformEnd(node.id, e);
            }}
            onTransform={isFlowchartShape ? (e: any) => {
              const target = e.target;
              const scaleX = target.scaleX();
              const scaleY = target.scaleY();
              const nodeData = nodes.find(n => n.id === node.id);
              if (!nodeData) return;
              const nextW = Math.max(20, (nodeData.w || 140) * scaleX);
              const nextH = Math.max(20, (nodeData.h || 80) * scaleY);
              setFlowchartLiveSizes((prev) => ({ ...prev, [node.id]: { w: nextW, h: nextH } }));
              const kn = target;
              const inner = (kn as any).findOne && (kn as any).findOne('.rotation-group');
              const rectLike = inner && inner.getChildren && inner.getChildren()[0];
              if (rectLike && rectLike.width && rectLike.height) {
                rectLike.width(nextW);
                rectLike.height(nextH);
              }
              try { target.scaleX(1); target.scaleY(1); } catch {}
            } : undefined}
            liveSize={isFlowchartShape ? flowchartLiveSizes[node.id] : undefined}
            onStartTextEdit={onStartTextEdit ? (textNode: any) => onStartTextEdit(node.id, 'text', textNode) : undefined}
            editing={!!isEditingThis}
            canWrite={canWrite}
            ref={getStableRefCb(node.id)}
          />
          {/* GRC badges + tag chips render whenever the shape carries
              controls/evidence/findings data. GrcBadges + ProcessTagBadges
              each check the node payload internally and return null when
              there's nothing to show, so removing the outer diagramType
              gate is safe and exposes assurance signals on process,
              swimlane, repository, and any other diagram that reuses
              flowchart/BPMN shapes. */}
          <GrcBadges node={node} visibility={processOverlayProps} />
          <ProcessTagBadges
            node={node}
            visible={processOverlayProps?.tags ?? true}
            reserveDependencyRow={processOverlayProps?.dependencies ?? true}
          />
          </Group>
        );
      }

      if (node.type === 'frame') {
        return (
          <FrameNode
            key={node.id}
            node={node}
            selected={selectedIds.has(node.id)}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
          />
        );
      }

      if (node.type === 'table') {
        const explicitVersion = (node as any)?.version ?? (node as any)?.__shapeVersion ?? (node.attrs as any)?.__shapeVersion;
        const defaultVersion = VersionedComponentRegistry.getDefaultVersion('table', 'shape');
        const effectiveVersion = typeof explicitVersion === 'number' ? explicitVersion : defaultVersion;
        const Component: any = VersionedComponentRegistry.getComponent('table', effectiveVersion, 'shape') || TableShapeNode;
        return (
          <Component
            key={node.id}
            node={node}
            selected={selectedIds.has(node.id)}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
            onStartCellTextEdit={
              onStartTableCellTextEdit
                ? (cellKey: TableCellKey, textNode: Konva.Text) => onStartTableCellTextEdit(node.id, cellKey, textNode)
                : undefined
            }
            editingCellKey={
              editingTextState?.nodeId === node.id
                ? (editingTextState.tableCellKey ?? null)
                : null
            }
            canWrite={canWrite}
            ref={getStableRefCb(node.id)}
          />
        );
      }

      if (node.type === 'image') {
        return (
          <ImageShapeNode
            key={node.id}
            node={node}
            diagramId={diagramId}
            selected={selectedIds.has(node.id)}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
            onDoubleClick={onNodeDoubleClick ? (e: any) => onNodeDoubleClick(node.id, e) : undefined}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
            canWrite={canWrite}
            ref={getStableRefCb(node.id)}
          />
        );
      }

      if (node.type === 'pdf') {
        return (
          <PdfShapeNode
            key={node.id}
            node={node}
            onDragStart={(pos: any) => onDragStart(node.id, pos)}
            onDragMove={(pos: any) => onDragMove(node.id, pos)}
            onDragEnd={(pos: any) => onDragEnd(node.id, pos)}
            onClick={(e: any) => onNodeClick(node.id, e)}
            onDoubleClick={onNodeDoubleClick ? (e: any) => onNodeDoubleClick(node.id, e) : undefined}
            onTransformStart={(e: any) => onTransformStart(node.id, e)}
            onTransformEnd={(e: any) => onTransformEnd(node.id, e)}
            canWrite={canWrite}
            ref={getStableRefCb(node.id)}
          />
        );
      }

      // Manifest-driven fallback for hybrid domains (Option C).
      // Hybrid domains (genogram, org, flowchart) use hardcoded React
      // components above for their primary shapes but can still ship new
      // shape types via the manifest system. This branch catches any node
      // type that didn't match a hardcoded handler but has a registered
      // shape manifest. See tryManifestRender() for the lookup strategy.
      if (diagramType) {
        const rendered = tryManifestRender(
          node,
          diagramType,
          selectedIds.has(node.id),
          { onDragStart, onDragMove, onDragEnd, onClick: onNodeClick, onDoubleClick: onNodeDoubleClick, onStartTextEdit },
          scoringResult,
          focusedRuleId,
          lensGetNodeStyle,
          editingTextState,
          canWrite,
        );
        if (rendered) return rendered;
      }

      // Additional node types can be added here
      console.warn('[NodesRenderer] unhandled node type:', node.type, 'id:', node.id);
      return null;
    }).filter(Boolean);

    // Wrap ghost-opacity nodes (added-since shapes in version preview) in a faded Group
    const ghostIds = new Set<string>();
    for (const node of ordered) {
      if ((node as any)._ghostOpacity) {
        ghostIds.add(node.id);
      }
    }
    if (ghostIds.size === 0) return elements;

    return elements.map((el) => {
      const reactEl = el as React.ReactElement<{ id?: string }>;
      const key = reactEl.key as string;
      // react-konva elements use the node id as key
      if (key && ghostIds.has(key)) {
        const opacity = (nodes.find((n) => n.id === key) as any)?._ghostOpacity ?? 0.3;
        return (
          <Group key={`ghost-${key}`} opacity={opacity} listening={false}>
            {reactEl}
          </Group>
        );
      }
      return el;
    });
  }, [nodes, edges, selectedIds, registerNodeRef, onNodeClick, onNodeDoubleClick, onDragStart, onDragMove, onDragEnd, onTransformStart, onTransform, onTransformEnd, onStartTextEdit, onStartTableCellTextEdit, editingTextState, projection, chartCanWrite, projectionLocked, lockProjectedNodePlacement, getScenarioStatus, getViewRuleStyle, isViewRuleNodeVisible, getViewRuleNodeOpacity, diagramId, diagramType, scoringResult, focusedRuleId, lensGetNodeStyle, getOrgComponent, computedReportCounts, getTenureFill, orgReportingTargets, onToggleOrgCollapse, enableOrgCollapseAnimation, orgExitNodeIds, orgNodeViewRuleStateById, getStablePositionHandlers]);

  // Note: Highlighting is now handled directly by shape components via useHighlightStore
  // Each org shape reads from the store and modifies its own stroke color/width

  return (
    <>
      {renderedNodes}
      {diagramType === 'org' ? (
        <OrgCollapsedDotGrids
          nodes={nodes}
          edges={edges}
          selectedIds={selectedIds}
          computedReportCounts={computedReportCounts}
          collapsedNodeIds={collapsedNodeIds}
          preCollapseEdges={preCollapseEdges}
          onToggleOrgCollapse={onToggleOrgCollapse}
          onExpandOrgCollapsedBranch={onExpandOrgCollapsedBranch}
          getCsvRecord={getCsvRecord}
          getCsvChildren={getCsvChildren}
          getCsvSecondaryManager={getCsvSecondaryManager}
          lensPalette={lensPalette}
        />
      ) : null}
    </>
  );
}, areNodesRendererPropsEqual);

NodesRenderer.displayName = 'NodesRenderer';
